// react
import React from "react";
import { useContext } from "react";

// mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { Skeleton } from "@mui/material";

// style
import useStyles from "./dashboardCardStyles";

// context
import { DeviceContext } from "../../../Context/deviceContext/deviceContext";

// Card-Header
const CardHeader = () => {
  const classes = useStyles();
  const { deviceData } = useContext(DeviceContext);
  return (
    <div>
      <Grid container sx={{ height: "2em", padding: "10px", mb: "10px" }}>
        <Grid item container>
          <Grid
            item
            xs
            zeroMinWidth
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              marginBottom: "3px",
            }}
          >
            {deviceData.gateway_name.length > 14 ? (
              <Tooltip title={deviceData.gateway_name} placement="bottom">
                <Typography sx={{ fontSize: "15px", fontWeight: "bold" }}>
                  {deviceData.gateway_name.slice(0, 14) + "..."}
                </Typography>
              </Tooltip>
            ) : (
              <Typography sx={{ fontSize: "15px", fontWeight: "bold" }}>
                {deviceData.gateway_name}
              </Typography>
            )}
          </Grid>
          <Grid item>
            <Chip
              variant="outlined"
              label="available"
              color="success"
              size="small"
              sx={{ height: "20px", fontFamily: "Poppins, sans-serif" }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Divider />
    </div>
  );
};

// Card-Content
const CardContent = () => {
  const classes = useStyles();
  const { deviceData } = useContext(DeviceContext);

  return (
    <Box sx={{ margin: "5px", padding: "0px 8px" }}>
      <Box sx={{ mt: 1 }}>
        <Grid container sx = {{ display : 'flex' ,alignItems : 'center'}}>
          <Grid item xs={5} sx={{ display: "flex", justifyContent: "start" }}>
            <span className={classes.dataKey}>EUI</span>
          </Grid>
          <Grid item>
            {deviceData.dev_eui.length > 16 ? (
              <Tooltip title={deviceData.dev_eui} placement="bottom">
                <span
                  className={classes.dataValue}
                  style={{ fontWeight: "400" }}
                >
                  : {deviceData.dev_eui.slice(0, 16) + "..."}
                </span>
              </Tooltip>
            ) : (
              <span className={classes.dataValue} style={{ fontWeight: "400" }}>
                : {deviceData.dev_eui}
              </span>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: "2px" }}>
        <Grid container sx = {{ display : 'flex' ,alignItems : 'center'}}>
          <Grid item xs={5} sx={{ display: "flex", justifyContent: "start" }}>
            <span className={classes.dataKey}>Type</span>
          </Grid>
          <Grid item>
            {deviceData.gateway_type.length > 14 ? (
              <Tooltip title={deviceData.gateway_type} placement="bottom">
                <span
                  className={classes.dataValue}
                  style={{ fontWeight: "400" }}
                >
                  : {deviceData.gateway_type.slice(0, 14) + "..."}
                </span>
              </Tooltip>
            ) : (
              <span className={classes.dataValue} style={{ fontWeight: "400" }}>
                : {deviceData.gateway_type}
              </span>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: "2px" }}>
        <Grid container sx = {{ display : 'flex' ,alignItems : 'center'}}>
          <Grid item xs={5} sx={{ display: "flex", justifyContent: "start" }}>
            <span className={classes.dataKey}>App</span>
          </Grid>
          <Grid item>
            {deviceData.app_name.length > 14 ? (
              <Tooltip title={deviceData.app_name} placement="top">
                <span className={classes.dataValue}>
                  : {deviceData.app_name.slice(0, 14) + "..."}
                </span>
              </Tooltip>
            ) : (
              <span className={classes.dataValue} style={{ fontWeight: "400" }}>
                : {deviceData.app_name}
              </span>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: "2px" }}>
        <Grid container sx = {{ display : 'flex' ,alignItems : 'center'}}>
          <Grid item xs={5} sx={{ display: "flex", justifyContent: "start" }}>
            <span className={classes.dataKey}>Battery</span>
          </Grid>
          <Grid item>
            <span className={classes.dataValue} style={{ fontWeight: "400" }}>
              :{" "}
              {deviceData.battery_level
                ? deviceData.battery_level.slice(0, 15) + " ...".toFixed(2)
                : "None"}
            </span>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: "2px" }}>
        <Grid container sx = {{ display : 'flex' ,alignItems : 'center'}}>
          <Grid item xs={5} sx={{ display: "flex", justifyContent: "start" }}>
            <span className={classes.dataKey}>Payload Size</span>
          </Grid>
          <Grid item>
            <span className={classes.dataValue} style={{ fontWeight: "400" }}>
              :{" "}
              {deviceData.payload_size
                ? deviceData.payload_size.toFixed(2) + " KB"
                : "None"}
            </span>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: "2px" }}>
        <Grid container sx = {{ display : 'flex' ,alignItems : 'center'}}>
          <Grid item xs={5} sx={{ display: "flex", justifyContent: "start" }}>
            <span className={classes.dataKey}>Frequency</span>
          </Grid>
          <Grid item>
            <span className={classes.dataValue} style={{ fontWeight: "400" }}>
              :{" "}
              {deviceData.interval ? deviceData.interval + " min once" : "None"}
            </span>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: "2px" }}>
        <Grid container sx = {{ display : 'flex' ,alignItems : 'center'}}>
          <Grid item xs={5} sx={{ display: "flex", justifyContent: "start" }}>
            <span className={classes.dataKey}>Total Payloads</span>
          </Grid>
          <Grid item>
            <span className={classes.dataValue} style={{ fontWeight: "400" }}>
              : {deviceData.data_points ? deviceData.data_points : "None"}
            </span>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

// Card-Footer
const CardFooter = () => {
  const { deviceData } = useContext(DeviceContext);
  const date = new Date(deviceData.last_seen);
  return (
    <Box sx={{height : '20%'}}>
      <Divider sx={{ backgroundColor: "#cfd8dc" }} />
      <Grid
        container
        sx={{
          display: "flex",
          justifyContent: "end",
          alignContent: "center",
          padding: "7px",
        }}
      >
        <Typography sx={{ fontSize: "12px", fontWeight: "500" }}>
          Last seen:
          {deviceData.last_seen
            ? `${date.toDateString().slice(3, 15)}`
            : "None"}
        </Typography>
      </Grid>
    </Box>
  );
};

// DeviceCard-Component
const DeviceCard = (props) => {
  const classes = useStyles();
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div sx={{ display: "grid" }}>
        <div>
          {props.isLoading ? (
            <Skeleton
              variant="rounded"
              width="300px"
              height="270px"
              animation="wave"
              sx={{ marginBottom: { sm: "-60px" } }}
            />
          ) : (
            <Card
              className={classes.root}
              sx={{
                borderRadius: 2,
                height: "17em",
                width: "300px",
                padding: "0px",
              }}
            >
              <CardHeader />
              <CardContent />
              <CardFooter />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceCard;

